import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import EditarPerfilModal from "../components/Perfil/EditarPerfilModal";
import ReiniciarPasswordModal from "../components/Perfil/ReiniciarPasswordModal";
import { useAuth } from "../auth/AuthContext";

const Perfil = () => {
  const { authFetch, bootstrapping, setUser, logout } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [userData, setUserData] = useState(null);
  const [countryOptions, setCountryOptions] = useState([]);

  const [isOpen, setIsOpen] = useState(false);
  const [isResetOpen, setIsResetOpen] = useState(false);

  // 1) Traer perfil desde BD
  useEffect(() => {
    const fetchProfile = async () => {
      setError("");
      if (bootstrapping) return;

      try {
        setLoading(true);

        const res = await authFetch("/api/auth/me", {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });

        const data = await res.json();

        if (!res.ok) {
          throw new Error(data?.error || "Could not load profile.");
        }

        const u = data.user;

        const mapped = {
          first_name: u.first_name ?? "",
          second_name: u.second_name ?? "",
          last_name: u.last_name ?? "",
          email: u.email ?? "",
          userName: u.email ?? "",
          country: u.country ?? "",
          organization: u.organization_name ?? "",
        };

        setUserData(mapped);
        setUser?.(u);
      } catch (e) {
        console.error(e);
        setError(e.message || "Error loading profile.");
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [authFetch, bootstrapping, setUser]);

  useEffect(() => {
    let alive = true;

    const loadCountryOptions = async () => {
      try {
        const endpoints = ["/api/auth/country-labels", "/api/auth/admin/country-labels"];

        for (const endpoint of endpoints) {
          try {
            const res = await authFetch(endpoint, {
              method: "GET",
              headers: { "Content-Type": "application/json" },
            });

            if (!res.ok) continue;

            const data = await res.json().catch(() => ({}));
            const labels = Array.isArray(data?.labels) ? data.labels : [];

            const options = labels
              .map((item) => {
                const code = String(item?.country_code || item?.code || "").trim();
                const label = String(
                  item?.country_label || item?.label || code,
                ).trim();
                if (!code) return null;
                return { value: code, label };
              })
              .filter(Boolean);

            if (alive) {
              setCountryOptions(options);
            }
            return;
          } catch (err) {
            console.error(`Error loading countries from ${endpoint}:`, err);
          }
        }

        if (alive) {
          setCountryOptions([]);
        }
      } catch (err) {
        console.error("Load country options error:", err);
        if (alive) {
          setCountryOptions([]);
        }
      }
    };

    loadCountryOptions();

    return () => {
      alive = false;
    };
  }, [authFetch]);

  const canRender = useMemo(
    () => !bootstrapping && !!userData,
    [bootstrapping, userData],
  );

  // 2) Guardar cambios del modal -> BD
  const handleSave = async (updatedData) => {
    setError("");

    // ✅ UI optimista (se refleja al instante)
    setUserData(updatedData);
    setIsOpen(false);

    try {
      setLoading(true);

      // ✅ SOLO se permite actualizar esto en BD
      const payload = {
        first_name: updatedData.first_name,
        second_name: updatedData.second_name,
        last_name: updatedData.last_name,
        country: updatedData.country,
      };

      const res = await authFetch("/api/auth/me", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data?.error || "Could not save profile.");
      }

      // 🔁 Re-sincroniza desde lo que devolvió la BD
      const u = data.user;

      const mapped = {
        first_name: u.first_name ?? "",
        second_name: u.second_name ?? "",
        last_name: u.last_name ?? "",
        email: u.email ?? "",
        userName: u.email ?? "",
        country: u.country ?? "",
        organization: u.organization_name ?? "",
      };

      setUserData(mapped);
      setUser?.(u);

      // Fuerza recarga completa para refrescar estado global y datos del perfil.
      window.location.reload();
      return;
    } catch (e) {
      console.error(e);
      setError(e.message || "Error saving profile.");
      // opcional: si quieres, aquí podrías re-cargar el perfil para revertir UI
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSave = async (formData) => {
    setError("");

    try {
      setLoading(true);

      const res = await authFetch("/api/auth/me/password", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          currentPassword: formData.currentPassword,
          newPassword: formData.newPassword,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data.success) {
        throw new Error(data?.error || "Could not change password.");
      }

      // ✅ Cierra modal
      setIsResetOpen(false);

      // ✅ Cierra sesión (revoca cookies + limpia memoria)
      await logout();

      // ✅ manda al login
      navigate("/login", { replace: true });
    } catch (e) {
      console.error(e);
      // Puedes mostrarlo en el modal (mejor) o aquí:
      setError(e.message || "Error changing password.");
      // NO cierres el modal si falló
    } finally {
      setLoading(false);
    }
  };

  if (bootstrapping) {
    return (
      <div className="w-full p-6">
        <h1 className="text-3xl font-semibold text-[#0F0F0F] mb-6">Cuenta</h1>
        <div className="text-sm text-[#7E8081]">Loading session...</div>
      </div>
    );
  }

  if (loading && !canRender) {
    return (
      <div className="w-full p-6">
        <h1 className="text-3xl font-semibold text-[#0F0F0F] mb-6">Cuenta</h1>
        <div className="text-sm text-[#7E8081]">Cargando perfil...</div>
      </div>
    );
  }

  if (!canRender) {
    return (
      <div className="w-full p-6">
        <h1 className="text-3xl font-semibold text-[#0F0F0F] mb-6">Cuenta</h1>
        <div className="text-sm text-red-600">
          {error || "Could not load profile."}
        </div>
      </div>
    );
  }

  return (
    <div className="w-full p-4 sm:p-6">
      <h1 className="text-2xl sm:text-3xl font-semibold text-[#0F0F0F] mb-6">
        Account
      </h1>

      <div className="bg-white rounded-lg shadow-sm p-4 sm:p-8 max-full overflow-hidden">
        {error && <div className="mb-4 text-sm text-red-600">{error}</div>}

        <div className="space-y-4">
          <Row label="Name" value={userData.first_name} />
          <Row label="Second name" value={userData.second_name} />
          <Row label="Last name" value={userData.last_name} />
          <Row label="Email" value={userData.email} />
          <Row label="User name" value={userData.userName} />
          <Row
            label="Password"
            value={
              <button
                type="button"
                className="text-[#54B046] font-semibold underline underline-offset-2"
                onClick={() => setIsResetOpen(true)}
              >
                Reset
              </button>
            }
          />
          <Row label="Country or Region" value={userData.country} />
          <Row label="Organization" value={userData.organization} />
        </div>

        <div className="flex justify-end mt-8 sm:mt-10">
          <button
            onClick={() => setIsOpen(true)}
            className="px-5 py-2 bg-black text-white rounded-lg hover:bg-gray-800 disabled:opacity-60 disabled:cursor-not-allowed"
            disabled={loading}
          >
            Edit
          </button>
        </div>
      </div>

      {/* modales... */}
      {isOpen && (
        <EditarPerfilModal
          data={userData}
          countryOptions={countryOptions}
          onSave={handleSave}
          onClose={() => setIsOpen(false)}
          loading={loading}
        />
      )}

      {isResetOpen && (
        <ReiniciarPasswordModal
          onSave={handlePasswordSave}
          onClose={() => setIsResetOpen(false)}
          loading={loading}
          error={error}
        />
      )}
    </div>
  );
};

const Row = ({ label, value }) => (
  <div className="border-b border-gray-200 pb-3">
    <div className="grid grid-cols-1 sm:grid-cols-[300px_1fr] gap-1 sm:gap-4">
      <span className="text-gray-900 font-medium text-sm sm:text-[16px]">
        {label}
      </span>

      <span className="text-gray-600 text-sm sm:text-[16px] break-words sm:break-normal">
        {value}
      </span>
    </div>
  </div>
);

export default Perfil;
